import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import bandService from "../services/bandService";
import { buildBandErrorMessage } from "../utils/bandErrorMessage";

const MIN_BAND_NAME_LENGTH = 3;
const MAX_BAND_NAME_LENGTH = 128;

export default function useBandCreator({ onCreated, visibility = "PUBLIC" } = {}) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [draft, setDraft] = useState({
        name: "",
        description: "",
    });
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const resetDraft = useCallback(() => {
        setDraft({
            name: "",
            description: "",
        });
        setError(null);
    }, []);

    const open = useCallback(() => {
        resetDraft();
        setIsOpen(true);
    }, [resetDraft]);

    const close = useCallback(() => {
        if (isSubmitting) {
            return;
        }
        setIsOpen(false);
        resetDraft();
    }, [isSubmitting, resetDraft]);

    const handleNameChange = useCallback((value) => {
        setDraft((current) => ({ ...current, name: value }));
    }, []);

    const handleDescriptionChange = useCallback((value) => {
        setDraft((current) => ({ ...current, description: value }));
    }, []);

    const submit = useCallback(async (event) => {
        event?.preventDefault();
        setError(null);

        const trimmedName = draft.name.trim();
        if (trimmedName.length < MIN_BAND_NAME_LENGTH) {
            setError(t("home.bandTab.create.validation.name"));
            return null;
        }
        if (trimmedName.length > MAX_BAND_NAME_LENGTH) {
            setError(t("home.bandTab.create.validation.length"));
            return null;
        }

        setIsSubmitting(true);
        try {
            const created = await bandService.createBand({
                name: trimmedName,
                description: draft.description.trim() || undefined,
                visibility,
            });
            setIsOpen(false);
            resetDraft();

            if (typeof onCreated === "function") {
                try {
                    await onCreated(created, { name: trimmedName });
                } catch (callbackError) {
                    console.error("Band post-create handler failed", callbackError);
                }
            }

            return created;
        } catch (createError) {
            console.error("Failed to create band", createError);
            setError(
                buildBandErrorMessage(t, createError, "home.bandTab.errors.createFailed", {
                    conflictKey: "home.bandTab.errors.duplicateBandName",
                    notFoundKey: null,
                    capacityKey: null,
                })
            );
            return null;
        } finally {
            setIsSubmitting(false);
        }
    }, [draft.description, draft.name, onCreated, resetDraft, t, visibility]);

    return {
        isOpen,
        draft,
        error,
        isSubmitting,
        open,
        close,
        submit,
        handleNameChange,
        handleDescriptionChange,
        modalLabels: {
            title: t("home.bandTab.create.title"),
            subtitle: t("home.bandTab.create.subtitle"),
            nameLabel: t("home.bandTab.create.nameLabel"),
            namePlaceholder: t("home.bandTab.create.namePlaceholder"),
            descriptionLabel: t("home.bandTab.create.descriptionLabel"),
            descriptionPlaceholder: t("home.bandTab.create.descriptionPlaceholder"),
            cancelLabel: t("home.bandTab.actions.cancel"),
            createLabel: t("home.bandTab.create.cta"),
            creatingLabel: t("home.bandTab.create.creating"),
        },
    };
}
